import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { postUser } from "../../helper/http";
import { useLanguage } from "../../context/LanguageContext";
import CreateTableModal from "../../components/DynamicTable/Modals/CreateTableModal/CreateTableModal";
import styles from "./TablesListPage.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTable,
  faFont,
  faHashtag,
  faCheckSquare,
  faCalendar,
  faLink,
  faPaperclip,
  faCircle,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { ToastService } from "../../service/toast.service";
import { useMenu } from "../../context/MenuContext";
import { FIELD_TYPES } from "../../types/fieldTypes";

interface Field {
  id: number;
  name: string;
  file_type: string;
  module_id: number;
}

interface Table {
  id: number;
  name: string;
  table_name: string;
  template_type?: string;
  fieldsCount?: number;
  fields?: Field[];
  is_system?: boolean;
}

export default function TablesListPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { loadMenu } = useMenu();
  const [tables, setTables] = useState<Table[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadTables();
  }, []);

  const loadTables = async () => {
    try {
      setLoading(true);

      const tablesRes = await postUser("crud/list", {
        module: "module",
        page: "1",
        per_page: "100",
        sort_direction: "+",
        sort_field: "order",
      });

      const BATCH_SIZE = 5;
      const tablesWithStats: Table[] = [];

      for (let i = 0; i < tablesRes.data.length; i += BATCH_SIZE) {
        const batch = tablesRes.data.slice(i, i + BATCH_SIZE);

        const batchResults = await Promise.all(
          batch.map(async (table: Table) => {
            try {
              const fieldsRes = await postUser("crud/list", {
                module: "module_field",
                page: "1",
                per_page: "100",
                sort_direction: "+",
                sort_field: "order",
                filters: { module_id: +table.id },
              });

              return {
                ...table,
                fieldsCount: fieldsRes.total || 0,
                fields: fieldsRes.data || [],
              };
            } catch (fieldError: any) {
              console.error(
                "Error loading fields for table",
                table.id,
                ":",
                fieldError?.response?.data,
              );

              return {
                ...table,
                fieldsCount: 0,
                fields: [],
              };
            }
          }),
        );

        tablesWithStats.push(...batchResults);
      }

      setTables(tablesWithStats);
    } catch (error: any) {
      console.error("Error loading tables:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTable = async (name: string, dbName: string) => {
    try {
      const maxOrder =
        tables.length > 0
          ? Math.max(...tables.map((t) => (t as any).order || 0))
          : 0;

      const response = await postUser("crud/upsert", {
        module: "module",
        body: {
          name,
          table_name: dbName,
          order: maxOrder + 1,
        },
      });

      setShowCreateModal(false);

      ToastService.success(t("tableCreatedSuccess"));

      await loadMenu();

      const newTableId =
        response?.id || response?.data?.id || response?.body?.id;

      if (newTableId) {
        navigate(`/tables/${newTableId}`);
      } else {
        const updatedTablesRes = await postUser("crud/list", {
          module: "module",
          page: "1",
          per_page: "100",
          sort_direction: "+",
          sort_field: "order",
        });

        const newTable = updatedTablesRes.data.find(
          (t: Table) => t.table_name === dbName,
        );

        if (newTable) {
          navigate(`/tables/${newTable.id}`);
        } else {
          await loadTables();
        }
      }
    } catch (error: any) {
      console.error("Error creating table:", error);

      let errorMessage = t("tableCreationError");

      if (error?.response?.data) {
        const data = error.response.data;
        if (typeof data === "string") {
          errorMessage = data;
        } else {
          errorMessage = data.message || data.error || JSON.stringify(data);
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      ToastService.error(errorMessage);
      throw error;
    }
  };

  const handleTableClick = (table: Table) => {
    if ((table as any).is_system) return;
    navigate(`/tables/${table.id}`);
  };

  const getFieldTypeIcon = (fileType: string) => {
    switch (fileType) {
      case "string":
      case "string_disabled":
        return faFont;
      case "number":
      case "integer":
      case "int":
        return faHashtag;
      case "boolean":
        return faCheckSquare;
      case "date":
      case "datetime":
      case "timestamp":
        return faCalendar;
      case "fk":
        return faLink;
      case "file":
        return faPaperclip;
      default:
        return faCircle;
    }
  };

  const getFieldTypeLabel = (fileType: string): string => {
    if (fileType === "string_disabled") {
      return t("idLabel");
    }

    const fieldTypeMeta = FIELD_TYPES.find((ft) => ft.value === fileType);
    return fieldTypeMeta ? t(fieldTypeMeta.labelKey) : fileType;
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>{t("loading")}</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <p className={styles.subtitle}>{t("tablesSubtitle")}</p>
        </div>
        <button
          className={styles.createButton}
          onClick={() => setShowCreateModal(true)}
        >
          <FontAwesomeIcon icon={faPlus} />
          {t("createTable")}
        </button>
      </div>

      <div className={styles.grid}>
        {tables.map((table) => (
          <div
            key={table.id}
            className={`${styles.card} ${(table as any).is_system ? styles.cardSystem : ""}`}
            onClick={() => handleTableClick(table)}
          >
            <div className={styles.cardHeader}>
              <div className={styles.cardIcon}>
                <FontAwesomeIcon icon={faTable} size="2x" />
              </div>

              <div className={styles.cardInfo}>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <h3 className={styles.cardTitle}>{table.name}</h3>
                  {(table as any).is_system && (
                    <span className={styles.systemBadge}>System</span>
                  )}
                </div>
                <p className={styles.cardDbName}>{table.table_name}</p>
              </div>
            </div>

            <div className={styles.cardBody}>
              {table.fields && table.fields.length > 0 ? (
                <div className={styles.fieldsList}>
                  {table.fields.map((field) => (
                    <div key={field.id} className={styles.fieldItem}>
                      <span className={styles.fieldIcon}>
                        <FontAwesomeIcon
                          icon={getFieldTypeIcon(field.file_type)}
                        />
                      </span>
                      <span className={styles.fieldName}>{field.name}</span>
                      <span className={styles.fieldType}>
                        {getFieldTypeLabel(field.file_type)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.noFields}>{t("noFields")}</div>
              )}
            </div>

            <div className={styles.cardFooter}></div>
          </div>
        ))}
      </div>

      <CreateTableModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateTable}
      />
    </div>
  );
}
